import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogService } from '../../services/dialog.service';

/**
 * Modal genérico y reutilizable que reemplaza a window.alert/confirm/prompt
 * en toda la app. Se monta una única vez en el root (ver app.ts) y se
 * controla por completo desde DialogService: los componentes nunca lo
 * importan directamente, solo inyectan DialogService y llaman
 * alert()/confirm()/prompt().
 */
@Component({
  selector: 'app-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    @if (state().visible) {
      <div
        class="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4 animate-fade-in"
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-dialog-message"
        (keydown.escape)="onCancel()"
      >
        <div
          class="bg-white dark:bg-neutral-900 rounded-xl max-w-md w-full shadow-2xl p-6 space-y-4 animate-zoom-in"
        >
          @if (state().title) {
            <h2 class="text-lg font-bold text-slate-900 dark:text-white">
              {{ state().title }}
            </h2>
          }

          <p id="app-dialog-message" class="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-line">
            {{ state().message }}
          </p>

          @if (state().type === 'prompt') {
            <input
              type="text"
              autofocus
              [ngModel]="state().promptValue"
              (ngModelChange)="dialog.updatePromptValue($event)"
              (keydown.enter)="onConfirm()"
              [placeholder]="state().promptPlaceholder"
              class="w-full rounded-lg border border-slate-300 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          }

          <div class="flex justify-end gap-3 pt-2">
            @if (state().type !== 'alert') {
              <button
                type="button"
                (click)="onCancel()"
                class="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-neutral-800 transition-colors"
              >
                {{ state().cancelLabel }}
              </button>
            }
            <button
              type="button"
              (click)="onConfirm()"
              class="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
              [ngClass]="state().danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'"
            >
              {{ state().confirmLabel }}
            </button>
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    .animate-fade-in {
      animation: fadeIn 0.2s ease-out;
    }
    .animate-zoom-in {
      animation: zoomIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes zoomIn {
      from { opacity: 0; transform: scale(0.95); }
      to { opacity: 1; transform: scale(1); }
    }
  `],
})
export class DialogComponent {
  protected readonly dialog = inject(DialogService);
  protected readonly state = this.dialog.state;

  protected onCancel(): void {
    const type = this.state().type;
    this.dialog.respond(type === 'prompt' ? null : false);
  }

  protected onConfirm(): void {
    const type = this.state();
    if (type.type === 'prompt') {
      this.dialog.respond(type.promptValue);
    } else if (type.type === 'confirm') {
      this.dialog.respond(true);
    } else {
      this.dialog.respond(undefined);
    }
  }
}
